// src/services/pdf/generateFactura.js
// Factura Ordinaria (Final) — documento legal para declarar a Hacienda.
// Muestra el total bruto y deduce el anticipo ya pagado (la señal cobrada).

import { jsPDF } from "jspdf";
import {
    preloadLogos,
    drawMinimalistHeader,
    drawSnapshot,
    drawSectionTitle,
    checkPageBreak,
    drawFooter,
} from "./pdfBranding.js";
import {
    COMPANY,
    COLORS,
    IVA_RATE,
    formatCurrency,
    formatDate,
    todayFormatted,
    generateDocNumber,
    safeFileName,
} from "./pdfConfig.js";

/**
 * Genera la Factura Final Ordinaria restando el anticipo ya pagado.
 * @param {object} event       - Datos del evento
 * @param {object} contact     - Contacto principal { name, email, phone, nif }
 * @param {object} companyData - Datos de la empresa (CIF, IBAN, etc.)
 * @param {string} docNumber   - Número de documento ya generado/incrementado
 * @param {object} invoice     - { advanceTotal }: señal cobrada con IVA (pagos de
 *                               tipo "advance"); 0 si no hay
 */
export async function generateFacturaPDF(event, contact = {}, companyData = {}, docNumber = "", invoice = {}) {

    if (!event) return;

    await preloadLogos();
    const doc = new jsPDF({ compress: true });
    const finalDocNumber = docNumber || generateDocNumber("F"); 
    const pageWidth = doc.internal.pageSize.getWidth();


    // ── Cálculos financieros ────────────────────────────────────────────────
    const totalBase       = Number(event.amount || 0);
    const totalIva        = totalBase * IVA_RATE;
    const totalBruto      = totalBase + totalIva;

    // La señal pactada es un porcentaje del precio, asi que se descuenta lo
    // cobrado de verdad y no una cifra fija.
    const signalAmount    = parseFloat((Number(invoice.advanceTotal) || 0).toFixed(2));
    const signalBase      = parseFloat((signalAmount / (1 + IVA_RATE)).toFixed(2));
    const signalIva       = parseFloat((signalAmount - signalBase).toFixed(2));

    const netBase         = parseFloat((totalBase - signalBase).toFixed(2));
    const netIva          = parseFloat((totalIva  - signalIva).toFixed(2));
    const netTotal        = parseFloat((totalBruto - signalAmount).toFixed(2));

    // === A. CABECERA MINIMALIST ===
    let y = drawMinimalistHeader(doc, { docNumber: finalDocNumber, company: companyData });


    // Título del documento
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(40, 40, 40);
    doc.text("FACTURA", pageWidth / 2, y, { align: "center" });
    y += 12;

    // === B. SNAPSHOT DE PARTES ===
    y = drawSnapshot(doc, y, {
        client: [
            contact.name || event.client_name_legacy || "—",
            `NIF/CIF: ${contact.nif || "________________"}`,
            contact.fiscal_address || "",
            `${contact.fiscal_zip || ""} ${contact.fiscal_city || ""}`.trim() || contact.email || "—",
        ],

        details: [
            `Nº Factura: ${finalDocNumber}`,
            `Fecha emisión: ${todayFormatted()}`,
            `Fecha servicio: ${formatDate(event.date)}`,
        ],
        event: [
            `Tipo: ${capitalize(event.type || "Evento")}`,
            `Ubicación: ${event.city || event.location || "—"}`,
            `Referencia: ${event.client_name_legacy || "—"}`,
        ],
    });

    y += 8;

    // === C. TABLA DE FACTURACIÓN ===
    y = drawSectionTitle(doc, y, "DETALLE DE FACTURACIÓN");

    const tableX   = 15;
    const tableW   = pageWidth - 30;
    const col = {
        desc:  tableX + 4,
        base:  tableX + tableW - 90,
        iva:   tableX + tableW - 55,
        total: tableX + tableW - 5,
    };

    // Cabecera
    doc.setFillColor(40, 40, 45);
    doc.rect(tableX, y - 4, tableW, 8, "F");
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("CONCEPTO", col.desc, y);
    doc.text("BASE IMPONIBLE", col.base, y, { align: "right" });
    doc.text(`IVA (${IVA_RATE * 100}%)`, col.iva, y, { align: "right" });
    doc.text("TOTAL", col.total, y, { align: "right" });
    y += 10;

    const rowHeight = 7;
    const eventDesc = `Servicios musicales · ${capitalize(event.type || "Evento")} ${formatDate(event.date)}`;

    // Fila 1 — Total del servicio
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.TEXT);
    doc.setFillColor(255, 255, 255);
    doc.rect(tableX, y - 4, tableW, rowHeight, "F");
    doc.text(eventDesc, col.desc, y, { maxWidth: tableW - 100 });
    doc.text(formatCurrency(totalBase),  col.base, y, { align: "right" });
    doc.text(formatCurrency(totalIva),   col.iva,  y, { align: "right" });
    doc.setFont("helvetica", "bold");
    doc.text(formatCurrency(totalBruto), col.total, y, { align: "right" });
    y += rowHeight + 1;

    // Fila 2 — Anticipo (negativo), solo si se cobro senal
    if (signalAmount > 0) {
        doc.setFillColor(252, 252, 253);
        doc.rect(tableX, y - 4, tableW, rowHeight, "F");
        doc.setFont("helvetica", "italic");
        doc.setTextColor(100, 100, 110);
        doc.text("Anticipo recibido (Señal confirmación — pagado)", col.desc, y);
        doc.text(`-${formatCurrency(signalBase)}`, col.base, y, { align: "right" });
        doc.text(`-${formatCurrency(signalIva)}`,  col.iva,  y, { align: "right" });
        doc.setFont("helvetica", "bold");
        doc.text(`-${formatCurrency(signalAmount)}`, col.total, y, { align: "right" });
        y += rowHeight;
    }
    y += 3;

    // Línea rosa separadora
    doc.setDrawColor(...COLORS.PRIMARY);
    doc.setLineWidth(0.5);
    doc.line(tableX, y, pageWidth - 15, y);
    y += 7;

    // Fila TOTAL A PERCIBIR
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.PRIMARY);
    doc.text("TOTAL A PERCIBIR", col.desc, y);
    doc.text(formatCurrency(netBase),  col.base,  y, { align: "right" });
    doc.text(formatCurrency(netIva),   col.iva,   y, { align: "right" });
    doc.setFontSize(12);
    doc.text(formatCurrency(netTotal), col.total, y, { align: "right" });
    y += 16;

    // === D. FORMA DE PAGO ===
    y = checkPageBreak(doc, y, 38);

    doc.setFillColor(250, 250, 252);
    doc.roundedRect(tableX, y, tableW, 30, 2, 2, "F");
    doc.setDrawColor(230, 230, 235);
    doc.setLineWidth(0.2);
    doc.roundedRect(tableX, y, tableW, 30, 2, 2, "S");

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.DARK);
    doc.text("FORMA DE PAGO", tableX + 6, y + 7);

    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.TEXT);
    doc.text("Transferencia Bancaria al día del servicio", tableX + 6, y + 14);
    doc.setFont("helvetica", "bold");
    doc.text(`IBAN: ${companyData.iban || COMPANY.iban}`, tableX + 6, y + 20);
    doc.setFont("helvetica", "normal");
    doc.text(`Beneficiario: ${companyData.name || COMPANY.name}`, tableX + 6, y + 26);

    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.PRIMARY);
    doc.text(`Importe: ${formatCurrency(netTotal)}`, pageWidth - 20, y + 20, { align: "right" });
    y += 38;

    // === E. PIE LEGAL ===
    y = checkPageBreak(doc, y, 12);
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(160, 160, 160);
    doc.text(
        "Factura sujeta a IVA general (21%) según artículo 90 Ley 37/1992. " +
        "Conserve este documento como justificante del servicio prestado.",
        tableX, y, { maxWidth: tableW }
    );

    // === FOOTER ===
    drawFooter(doc);

    // === GUARDAR ===
    const safeName = safeFileName(event.client_name_legacy);
    doc.save(`Factura_${safeName}_${finalDocNumber}.pdf`);

}

// ─── Helpers ───────────────────────────────────────────────────────────────
function capitalize(str) {
    return str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
}
